/**
 * SYNCED FROM SERVER — do not edit by hand.
 *
 * Source: `server/src/api/ai-models/ai-models.contract.ts` +
 * `server/src/domain/schemas/ai-catalog/*` (the model taxonomy is owned by the
 * server domain; this file is the self-contained client structural copy).
 *
 * NOTE: `sync:contracts` is not used for this contract — it is mirrored by hand
 * because the model taxonomy is a discriminated union sourced from the domain.
 *
 * The server is the source of truth; this file exists so the web SPA can import
 * the zod schemas + contract types without reaching into the server workspace.
 */

/**
 * AI Models Contract
 *
 * The AI-model catalog: a Zod-first discriminated union over 8 model types.
 * OpenRouter (the only source today) yields `type: 'text'`; the `TextModel`
 * member is a superset carrying OpenRouter metadata (endpoints, variant,
 * supported_parameters, architecture, …). The other members are future-proofing.
 */

import { oc } from "@orpc/contract";
import * as z from "zod";
import { commonErrors } from "./base";

// =============================================================================
// Model type discriminant
// =============================================================================

export const ModelTypeSchema = z.enum([
	"text",
	"image",
	"video",
	"inpaint",
	"embedding",
	"tts",
	"asr",
	"upscale",
]);

/**
 * Known model variant suffixes (e.g., :free, :nitro). The schema accepts any
 * string for forward-compatibility with new OpenRouter variants.
 */
export const KNOWN_MODEL_VARIANTS = [
	"free",
	"extended",
	"exacto",
	"nitro",
	"floor",
	"thinking",
	"online",
] as const;

export const ModelVariantSchema = z.string();

export const KNOWN_OPENROUTER_PARAMETERS = [
	"temperature",
	"top_p",
	"top_k",
	"min_p",
	"top_a",
	"frequency_penalty",
	"presence_penalty",
	"repetition_penalty",
	"max_tokens",
	"logit_bias",
	"logprobs",
	"top_logprobs",
	"seed",
	"response_format",
	"structured_outputs",
	"stop",
	"tools",
	"tool_choice",
	"parallel_tool_calls",
	"include_reasoning",
	"reasoning",
	"web_search_options",
	"verbosity",
] as const;

export const OpenRouterParameterSchema = z.string();

// =============================================================================
// OpenRouter carry-over sub-schemas (text superset)
// =============================================================================

export const ModelPricingSchema = z.object({
	prompt: z.string().optional(),
	completion: z.string().optional(),
});

export const EndpointPricingSchema = z.object({
	prompt: z.union([z.string(), z.number()]),
	completion: z.union([z.string(), z.number()]),
	request: z.union([z.string(), z.number()]).optional(),
	image: z.union([z.string(), z.number()]).optional(),
	image_token: z.union([z.string(), z.number()]).optional(),
	image_output: z.union([z.string(), z.number()]).optional(),
	audio: z.union([z.string(), z.number()]).optional(),
	input_audio_cache: z.union([z.string(), z.number()]).optional(),
	web_search: z.union([z.string(), z.number()]).optional(),
	internal_reasoning: z.union([z.string(), z.number()]).optional(),
	input_cache_read: z.union([z.string(), z.number()]).optional(),
	input_cache_write: z.union([z.string(), z.number()]).optional(),
	discount: z.number().optional(),
});

export const OpenRouterEndpointSchema = z.object({
	name: z.string(),
	model_name: z.string(),
	context_length: z.number(),
	pricing: EndpointPricingSchema,
	provider_name: z.string(),
	tag: z.string(),
	quantization: z
		.union([z.string(), z.record(z.string(), z.unknown())])
		.nullish(),
	max_completion_tokens: z.number().nullish(),
	max_prompt_tokens: z.number().nullish(),
	supported_parameters: z.array(OpenRouterParameterSchema).optional(),
	status: z.union([z.string(), z.number()]).nullish(),
	uptime_last_30m: z.number().nullish(),
	supports_implicit_caching: z.boolean().optional(),
});

export const ModelArchitectureSchema = z.object({
	modality: z.string().optional(),
	tokenizer: z.string().optional(),
	instruct_type: z.string().optional(),
	input_modalities: z.array(z.string()).optional(),
	output_modalities: z.array(z.string()).optional(),
});

export const TopProviderSchema = z.object({
	max_completion_tokens: z.number().optional(),
	is_moderated: z.boolean().optional(),
});

export const PerRequestLimitsSchema = z.object({
	prompt_tokens: z.string().optional(),
	completion_tokens: z.string().optional(),
});

// =============================================================================
// Shared base fields
// =============================================================================

export const DeprecationSchema = z.object({ date: z.string() });

const baseModelFields = {
	id: z.string(),
	name: z.string(),
	provider: z.string().optional(),
	created: z.number().optional(),
	description: z.string().optional(),
	privacy: z.enum(["private", "anonymized"]).optional(),
	offline: z.boolean().optional(),
	betaModel: z.boolean().optional(),
	modelSource: z.string().optional(),
	traits: z.array(z.string()).optional(),
	deprecation: DeprecationSchema.optional(),
};

// =============================================================================
// Per-type model schemas
// =============================================================================

/** Text/chat model — superset carrying OpenRouter metadata. */
export const TextModelSchema = z.object({
	...baseModelFields,
	type: z.literal("text"),
	context_length: z.number().optional(),
	pricing: ModelPricingSchema.optional(),
	maker: z.string().optional(),
	model_name: z.string().optional(),
	variant: ModelVariantSchema.optional(),
	supported_parameters: z.array(OpenRouterParameterSchema).optional(),
	architecture: ModelArchitectureSchema.optional(),
	top_provider: TopProviderSchema.optional(),
	per_request_limits: PerRequestLimitsSchema.optional(),
	endpoints: z.array(OpenRouterEndpointSchema).optional(),
});

export const ImagePricingSchema = z.object({
	generation: z.number().optional(),
	upscale_2x: z.number().optional(),
	upscale_4x: z.number().optional(),
	resolutions: z.record(z.string(), z.number()).optional(),
});
export const ImageConstraintsSchema = z.object({
	promptCharacterLimit: z.number().optional(),
	steps: z.object({ default: z.number(), max: z.number() }).optional(),
	widthHeightDivisor: z.number().optional(),
	aspectRatios: z.array(z.string()).optional(),
	defaultAspectRatio: z.string().optional(),
	resolutions: z.array(z.string()).optional(),
	defaultResolution: z.string().optional(),
});
export const ImageModelSchema = z.object({
	...baseModelFields,
	type: z.literal("image"),
	pricing: ImagePricingSchema,
	constraints: ImageConstraintsSchema.optional(),
	supportsWebSearch: z.boolean().optional(),
});

export const VideoConstraintsSchema = z.object({
	model_type: z.enum(["text-to-video", "image-to-video", "video"]).optional(),
	aspect_ratios: z.array(z.string()).optional(),
	resolutions: z.array(z.string()).optional(),
	durations: z.array(z.string()).optional(),
	audio: z.boolean().optional(),
	audio_configurable: z.boolean().optional(),
	audio_input: z.boolean().optional(),
	video_input: z.boolean().optional(),
});
export const VideoModelSchema = z.object({
	...baseModelFields,
	type: z.literal("video"),
	constraints: VideoConstraintsSchema.optional(),
	model_sets: z.array(z.string()).optional(),
});

export const InpaintPricingSchema = z.object({
	generation: z.number().optional(),
});
export const InpaintConstraintsSchema = z.object({
	aspectRatios: z.array(z.string()).optional(),
	combineImages: z.boolean().optional(),
});
export const InpaintModelSchema = z.object({
	...baseModelFields,
	type: z.literal("inpaint"),
	pricing: InpaintPricingSchema,
	constraints: InpaintConstraintsSchema.optional(),
});

export const EmbeddingPricingSchema = z.object({
	input: z.number().optional(),
	output: z.number().optional(),
});
export const EmbeddingModelSchema = z.object({
	...baseModelFields,
	type: z.literal("embedding"),
	pricing: EmbeddingPricingSchema,
});

export const TtsPricingSchema = z.object({ input: z.number().optional() });
export const TtsModelSchema = z.object({
	...baseModelFields,
	type: z.literal("tts"),
	pricing: TtsPricingSchema,
	voices: z.array(z.string()).optional(),
});

export const AsrPricingSchema = z.object({
	per_audio_second: z.number().optional(),
});
export const AsrModelSchema = z.object({
	...baseModelFields,
	type: z.literal("asr"),
	pricing: AsrPricingSchema,
});

export const UpscalePricingSchema = z.object({
	generation: z.number().optional(),
});
export const UpscaleModelSchema = z.object({
	...baseModelFields,
	type: z.literal("upscale"),
	pricing: UpscalePricingSchema,
});

// =============================================================================
// Discriminated union
// =============================================================================

export const AnyModelSchema = z.discriminatedUnion("type", [
	TextModelSchema,
	ImageModelSchema,
	VideoModelSchema,
	InpaintModelSchema,
	EmbeddingModelSchema,
	TtsModelSchema,
	AsrModelSchema,
	UpscaleModelSchema,
]);

export const AIModelsResponseSchema = z.object({
	data: z.array(AnyModelSchema).describe("List of available AI models"),
});

// =============================================================================
// Providers
// =============================================================================

export const OpenRouterProviderSchema = z.object({
	name: z.string().describe("Provider name"),
	slug: z.string().describe("Provider slug"),
	privacy_policy_url: z.string().nullish().describe("Privacy policy URL"),
	terms_of_service_url: z.string().nullish().describe("Terms of service URL"),
	status_page_url: z.string().nullish().describe("Status page URL"),
});

export const ProvidersResponseSchema = z.object({
	data: z
		.array(OpenRouterProviderSchema)
		.describe("List of available providers"),
});

// =============================================================================
// Benchmarks
// =============================================================================

export const ModelBenchmarkSchema = z.object({
	slug: z.string(),
	name: z.string(),
	creatorSlug: z.string().nullish(),
	creatorName: z.string().nullish(),
	releaseDate: z.string().nullish(),
	intelligenceIndex: z.number().nullish(),
	codingIndex: z.number().nullish(),
	mathIndex: z.number().nullish(),
	agenticIndex: z.number().nullish(),
	speedTokensPerSecond: z.number().nullish(),
	latencyFirstTokenSeconds: z.number().nullish(),
	priceBlendedUsdPer1m: z.number().nullish(),
});

export const ModelBenchmarksResponseSchema = z.object({
	data: z.array(ModelBenchmarkSchema),
	attributionUrl: z.string(),
});

// =============================================================================
// Error Definitions
// =============================================================================

const aiModelsErrors = {
	UNAUTHORIZED: commonErrors.UNAUTHORIZED,
	FETCH_FAILED: {
		message: "Failed to fetch AI models from provider",
		data: z.object({
			reason: z.string().optional(),
		}),
	},
	VALIDATION_FAILED: {
		message: "AI models response validation failed",
		data: z.object({
			issueCount: z.number().optional(),
		}),
	},
};

// =============================================================================
// Contract Definition
// =============================================================================

export const aiModelsContract = {
	list: oc
		.route({
			method: "GET",
			path: "/api/ai/models",
			summary: "List available AI models",
			description:
				"Retrieve all available AI models from OpenRouter with their metadata.",
			tags: ["AI Models"],
		})
		.input(z.object({}))
		.output(AIModelsResponseSchema)
		.errors(aiModelsErrors),

	listProviders: oc
		.route({
			method: "GET",
			path: "/api/ai/providers",
			summary: "List available AI providers",
			description:
				"Retrieve all available infrastructure providers from OpenRouter.",
			tags: ["AI Models"],
		})
		.input(z.object({}))
		.output(ProvidersResponseSchema)
		.errors(aiModelsErrors),
};

// =============================================================================
// Type Exports
// =============================================================================

export type AIModelsContract = typeof aiModelsContract;

export type ModelType = z.infer<typeof ModelTypeSchema>;

/** Known model variants (type-safe subset) */
export type KnownModelVariant = (typeof KNOWN_MODEL_VARIANTS)[number];
/** Model variant - any string for forward compatibility */
export type ModelVariant = z.infer<typeof ModelVariantSchema>;
/** Known OpenRouter parameters (type-safe subset) */
export type KnownOpenRouterParameter =
	(typeof KNOWN_OPENROUTER_PARAMETERS)[number];
/** OpenRouter parameter - any string for forward compatibility */
export type OpenRouterParameter = z.infer<typeof OpenRouterParameterSchema>;

export type OpenRouterEndpoint = z.infer<typeof OpenRouterEndpointSchema>;

export type TextModel = z.infer<typeof TextModelSchema>;
export type ImageModel = z.infer<typeof ImageModelSchema>;
export type VideoModel = z.infer<typeof VideoModelSchema>;
export type InpaintModel = z.infer<typeof InpaintModelSchema>;
export type EmbeddingModel = z.infer<typeof EmbeddingModelSchema>;
export type TtsModel = z.infer<typeof TtsModelSchema>;
export type AsrModel = z.infer<typeof AsrModelSchema>;
export type UpscaleModel = z.infer<typeof UpscaleModelSchema>;

/** Discriminated union of all model types. */
export type AnyModel = z.infer<typeof AnyModelSchema>;
export type AIModelsResponse = z.infer<typeof AIModelsResponseSchema>;
export type OpenRouterProvider = z.infer<typeof OpenRouterProviderSchema>;
export type ProvidersResponse = z.infer<typeof ProvidersResponseSchema>;
export type ModelBenchmark = z.infer<typeof ModelBenchmarkSchema>;
export type ModelBenchmarksResponse = z.infer<
	typeof ModelBenchmarksResponseSchema
>;
